import { useRef } from "react";
import type { ReactNode } from "react";

interface ReportAccordionItemProps {
    id: string;
    eyebrow?: ReactNode;
    title: ReactNode;
    summary?: ReactNode;
    meta?: ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
    className?: string;
}

export function preserveAnchorPosition(anchor: Element | null, action: () => void, durationMs = 360) {
    const previousTop = anchor?.getBoundingClientRect().top;
    action();

    if (!anchor || typeof previousTop !== "number") return;

    const startedAt = performance.now();
    const keepAnchored = () => {
        const nextTop = anchor.getBoundingClientRect().top;
        const offset = nextTop - previousTop;

        if (Math.abs(offset) > 0.5) {
            window.scrollBy({ top: offset, left: 0, behavior: "auto" });
        }

        if (performance.now() - startedAt < durationMs) {
            requestAnimationFrame(keepAnchored);
        }
    };

    requestAnimationFrame(keepAnchored);
}

export function ReportAccordionItem({
    id,
    eyebrow,
    title,
    summary,
    meta,
    open,
    onOpenChange,
    children,
    className = "",
}: ReportAccordionItemProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const panelId = `${id}-panel`;
    const triggerId = `${id}-trigger`;

    function toggleOpen() {
        preserveAnchorPosition(rootRef.current, () => onOpenChange(!open));
    }

    return (
        <div className={["rc-accordion-item", open ? "open" : "", className].filter(Boolean).join(" ")} ref={rootRef}>
            <button
                id={triggerId}
                type="button"
                className="rc-accordion-trigger"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={toggleOpen}
            >
                <span className="rc-accordion-title-wrap">
                    {eyebrow && <span className="rc-accordion-eyebrow">{eyebrow}</span>}
                    <span className="rc-accordion-title">{title}</span>
                    {summary && <span className="rc-accordion-summary">{summary}</span>}
                </span>
                <span className="rc-accordion-meta">
                    {meta}
                    <span className="material-icons md-18 rc-accordion-chevron" aria-hidden="true">expand_more</span>
                </span>
            </button>
            <div
                id={panelId}
                className="rc-accordion-panel"
                role="region"
                aria-labelledby={triggerId}
            >
                <div className="rc-accordion-body">
                    <div className="rc-accordion-body-inner">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
